import React, { useEffect, useState, useRef } from 'react';
import { pesos, numKm, nombrePropio, pctTxt } from './formato';
import { loadLeafletCDN } from './rutas';

const API_BASE = 'https://cyker.satelitax.com.ar/api';
const TEAL     = '#74C8D2';
const TEAL_DK  = '#4AAAB3';
const TEXT1    = '#E6EDF3';
const TEXT2    = '#7D8590';
const SURFACE  = '#161B22';
const SURFACE2 = '#1C2128';
const BORDER   = '#30363D';
const GREEN    = '#22C55E';
const YELLOW   = '#F5A623';
const RED      = '#EF4444';
const ROUTE_BLUE = '#1E88E5';
const OPT_ORANGE = '#FF6D00';

const CARD = {
  background: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: '16px',
  marginBottom: '12px',
  overflow: 'hidden',
};

function CykerHeader({ aditamento }) {
  return (
    <div style={{ textAlign: 'center', paddingTop: '20px', paddingBottom: '24px' }}>
      <img
        src="/logo_cyker_web.png"
        alt="Cyker"
        style={{
          width: '130px', height: 'auto',
          display: 'block', margin: '0 auto 10px',
          opacity: 1,
        }}
      />
      <div style={{ fontSize: '10px', color: TEXT2, letterSpacing: '4px', textTransform: 'uppercase' }}>
        Taxímetro Digital
      </div>
      {aditamento && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px',
          background: 'rgba(116,200,210,0.1)',
          border: '1px solid rgba(116,200,210,0.25)',
          borderRadius: '20px',
          padding: '6px 18px', marginTop: '12px',
        }}>
          <span style={{ fontSize: '18px' }}>🚕</span>
          <span style={{ color: TEAL, fontWeight: '700', fontSize: '16px', letterSpacing: '1px' }}>
            Taxi {aditamento}
          </span>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ finalizado }) {
  if (finalizado) {
    return (
      <div style={{
        display: 'inline-block', marginTop: '10px',
        background: 'rgba(34,197,94,0.12)',
        border: '1px solid rgba(34,197,94,0.3)',
        color: GREEN,
        fontSize: '11px', fontWeight: '700',
        padding: '4px 14px', borderRadius: '12px', letterSpacing: '1px',
      }}>
        ✓ VIAJE FINALIZADO
      </div>
    );
  }
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '10px',
      background: 'rgba(116,200,210,0.12)',
      border: '1px solid rgba(116,200,210,0.3)',
      color: TEAL,
      fontSize: '11px', fontWeight: '700',
      padding: '5px 14px', borderRadius: '12px', letterSpacing: '1px',
    }}>
      <span style={{
        width: '7px', height: '7px', borderRadius: '50%', background: TEAL,
        display: 'inline-block', animation: 'blink 1.5s infinite',
      }} />
      EN CURSO
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div style={{
      background: SURFACE, border: `1px solid ${BORDER}`,
      borderRadius: '12px', padding: '14px 8px',
      textAlign: 'center', marginBottom: 0,
    }}>
      <div style={{ fontSize: '15px', fontWeight: '700', color: TEXT1 }}>{value}</div>
      <div style={{ fontSize: '10px', color: TEAL, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '3px' }}>
        {label}
      </div>
    </div>
  );
}

function Stars({ value, onChange, disabled }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', padding: '4px 0' }}>
      {[1, 2, 3, 4, 5].map(n => {
        const filled = n <= (hovered || value);
        return (
          <button
            key={n}
            disabled={disabled}
            onClick={() => !disabled && onChange(n)}
            onMouseEnter={() => !disabled && setHovered(n)}
            onMouseLeave={() => !disabled && setHovered(0)}
            style={{
              background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer',
              padding: '2px', fontSize: '34px', lineHeight: 1,
              color: filled ? YELLOW : BORDER,
              transform: filled ? 'scale(1.1)' : 'scale(1)',
              transition: 'all 0.12s',
            }}
          >
            ★
          </button>
        );
      })}
    </div>
  );
}

function haversineM(a, b) {
  const R = 6371000;
  const dLat = (b[0] - a[0]) * Math.PI / 180;
  const dLng = (b[1] - a[1]) * Math.PI / 180;
  const x = Math.sin(dLat / 2) ** 2
    + Math.cos(a[0] * Math.PI / 180) * Math.cos(b[0] * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function segmentPathLength(positions, fromIdx, toIdx) {
  let total = 0;
  for (let i = fromIdx; i < toIdx; i++) total += haversineM(positions[i], positions[i + 1]);
  return total;
}

function osrmUrl(from, to) {
  return `https://router.project-osrm.org/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`;
}

async function reverseGeocode([lat, lng]) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`;
  const res = await fetch(url, { headers: { 'Accept-Language': 'es', 'User-Agent': 'CykerApp/1.0' } });
  const data = await res.json();
  const a = data.address || {};
  const road = a.road || a.pedestrian || a.footway || a.path || '';
  const num  = a.house_number ? ` ${a.house_number}` : '';
  const area = a.suburb || a.neighbourhood || a.city_district || a.quarter || a.city || a.town || a.village || '';
  return road ? `${road}${num}${area ? ', ' + area : ''}` : (data.display_name || '');
}

function App() {
  const raw = window.location.pathname.replace(/^\//, '').split('/').pop();
  const qrToken = raw || new URLSearchParams(window.location.search).get('token');

  const [trip, setTrip]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [now, setNow]               = useState(Date.now());

  const [rating, setRating]             = useState(0);
  const [ratingDone, setRatingDone]     = useState(false);
  const [ratingLoading, setRatingLoading] = useState(false);

  const [denunciaOpen, setDenunciaOpen] = useState(false);
  const [denunciaText, setDenunciaText] = useState('');
  const [denunciaDone, setDenunciaDone] = useState(false);
  const [denunciaLoading, setDenunciaLoading] = useState(false);

  const [positions, setPositions]         = useState(null);
  const [mapReady, setMapReady]           = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [osrmData, setOsrmData]           = useState(null);
  const [startAddress, setStartAddress]   = useState(null);
  const [endAddress, setEndAddress]       = useState(null);
  const mapContainerRef  = useRef(null);
  const mapInstanceRef   = useRef(null);
  const mapLoadStartedRef = useRef(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!qrToken) { setError('QR inválido.'); setLoading(false); return; }
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/viajes/qr/${qrToken}`, { headers: { Accept: 'application/json' } });
        if (!res.ok) { setError('Viaje no encontrado.'); setLoading(false); return; }
        const data = await res.json();
        setTrip(data);
        if (data.rating) { setRating(data.rating); setRatingDone(true); }
        if (data.denuncia) setDenunciaDone(true);
        setLoading(false);
      } catch (e) { setError('No se pudo conectar.'); setLoading(false); }
    };
    load();
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/viajes/qr/${qrToken}`, { headers: { Accept: 'application/json' } });
        if (res.ok) {
          const data = await res.json();
          setTrip(data);
          if (data.fin || data.estado === 'finalizado') clearInterval(interval);
        }
      } catch (e) {}
    }, 5000);
    return () => clearInterval(interval);
  }, [qrToken]);

  useEffect(() => {
    if (!trip || mapLoadStartedRef.current) return;
    const isFinalizado = !!(trip.fin || trip.estado === 'finalizado');
    if (!isFinalizado) return;
    mapLoadStartedRef.current = true;
    loadLeafletCDN(() => setMapReady(true));
    fetch(`${API_BASE}/viajes/qr/${qrToken}/posiciones`)
      .then(r => r.json())
      .then(data => {
        const pts = Array.isArray(data) ? data : [];
        setPositions(pts);
        if (pts.length >= 1) reverseGeocode(pts[0]).then(setStartAddress).catch(() => {});
        if (pts.length >= 2) reverseGeocode(pts[pts.length - 1]).then(setEndAddress).catch(() => {});
      })
      .catch(() => setPositions([]));
  }, [trip, qrToken]);

  useEffect(() => {
    if (!positions || positions.length < 2) return;
    if (!mapReady || !window.L) return;
    if (!mapContainerRef.current || mapInstanceRef.current) return;

    const L = window.L;
    // Vista previa quieta: al deslizar el dedo baja la pagina (antes arrastraba el mapa); tocarla abre /recorrido
    const map = L.map(mapContainerRef.current, { dragging: false, touchZoom: false, scrollWheelZoom: false, doubleClickZoom: false, boxZoom: false, keyboard: false, zoomControl: false, tap: false });
    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap', maxZoom: 19,
    }).addTo(map);

    const poly = L.polyline(positions, { color: ROUTE_BLUE, weight: 4, opacity: 0.9 }).addTo(map);

    const dotIcon = (color) => L.divIcon({
      html: `<div style="background:${color};width:12px;height:12px;border-radius:50%;border:2.5px solid white;box-shadow:0 1px 5px rgba(0,0,0,0.45)"></div>`,
      className: '', iconSize: [12, 12], iconAnchor: [6, 6],
    });

    const p0 = positions[0];
    const pN = positions[positions.length - 1];
    const realDistM = trip ? (parseFloat(trip.distancia_metros) || 0) : 0;

    L.marker(p0, { icon: dotIcon('#22C55E') }).addTo(map);
    L.marker(pN, { icon: dotIcon('#EF4444') }).addTo(map);
    map.fitBounds(poly.getBounds(), { padding: [28, 28] });
    setMapInitialized(true);

    if (realDistM <= 0) return;

    const distLinealTotal = haversineM(p0, pN);
    const isCircular = distLinealTotal < 300 || distLinealTotal / realDistM < 0.25;

    const drawOptimal = (coords) => {
      return L.polyline(coords, { color: OPT_ORANGE, weight: 3.5, opacity: 0.9 }).addTo(map);
    };

    const finalizeOsrm = (optLines, totalOptDistM) => {
      let bounds = poly.getBounds();
      optLines.forEach(l => { bounds = bounds.extend(l.getBounds()); });
      map.fitBounds(bounds, { padding: [28, 28] });
      const maxD = Math.max(realDistM, totalOptDistM);
      const pct = totalOptDistM > 0 ? Math.round((realDistM - totalOptDistM) / totalOptDistM * 100) : 0;
      setOsrmData({
        realKm: numKm(realDistM),
        optKm: numKm(totalOptDistM),
        pct,
        realBarPct: Math.round(realDistM / maxD * 100),
        optBarPct: Math.round(totalOptDistM / maxD * 100),
      });
    };

    if (!isCircular) {
      fetch(osrmUrl(p0, pN))
        .then(r => r.json())
        .then(data => {
          if (!data.routes || !data.routes[0]) return;
          const opt = data.routes[0];
          const coords = opt.geometry.coordinates.map(c => [c[1], c[0]]);
          const line = drawOptimal(coords);
          finalizeOsrm([line], opt.distance);
        })
        .catch(() => {});
    } else {
      let maxLinDist = 0, maxIdx = 0;
      for (let i = 1; i < positions.length; i++) {
        const d = haversineM(p0, positions[i]);
        if (d > maxLinDist) { maxLinDist = d; maxIdx = i; }
      }
      const pMax = positions[maxIdx];
      const seg1Dist = segmentPathLength(positions, 0, maxIdx);
      const seg2Dist = segmentPathLength(positions, maxIdx, positions.length - 1);
      const linSeg1 = haversineM(p0, pMax);
      const linSeg2 = haversineM(pMax, pN);
      const segs = [];
      if (seg1Dist > 200 && linSeg1 > 200) segs.push({ from: p0, to: pMax });
      if (seg2Dist > 200 && linSeg2 > 100) segs.push({ from: pMax, to: pN });
      if (segs.length === 0) return;
      Promise.all(
        segs.map(seg =>
          fetch(osrmUrl(seg.from, seg.to))
            .then(r => r.json())
            .then(data => {
              if (!data.routes || !data.routes[0]) return null;
              const opt = data.routes[0];
              const coords = opt.geometry.coordinates.map(c => [c[1], c[0]]);
              const line = drawOptimal(coords);
              return { line, optDist: opt.distance };
            })
            .catch(() => null)
        )
      ).then(results => {
        const valid = results.filter(Boolean);
        if (valid.length === 0) return;
        finalizeOsrm(valid.map(r => r.line), valid.reduce((s, r) => s + r.optDist, 0));
      });
    }
  }, [positions, mapReady, trip]);

  const submitRating = async (stars) => {
    setRating(stars);
    setRatingLoading(true);
    try {
      await fetch(`${API_BASE}/viajes/qr/${qrToken}/calificar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estrellas: stars }),
      });
      setRatingDone(true);
    } catch (e) {}
    setRatingLoading(false);
  };

  const submitDenuncia = async () => {
    if (!denunciaText.trim()) return;
    setDenunciaLoading(true);
    try {
      await fetch(`${API_BASE}/viajes/qr/${qrToken}/denunciar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ descripcion: denunciaText.trim() }),
      });
      setDenunciaDone(true);
      setDenunciaOpen(false);
    } catch (e) {}
    setDenunciaLoading(false);
  };

  const fmtElapsed = (fromISO, toMs) => {
    if (!fromISO) return '--:--';
    const diff = Math.max(0, Math.floor((toMs - new Date(fromISO).getTime()) / 1000));
    const h = Math.floor(diff / 3600), m = Math.floor((diff % 3600) / 60), s = diff % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const fmtDuration = (secs) => {
    if (!secs) return '--:--';
    const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const bgStyle = {
    minHeight: '100vh',
    background: '#0D1117',
    padding: '0 16px 40px',
    fontFamily: "'Segoe UI', Roboto, sans-serif",
    position: 'relative',
  };

  const glowStyle = {
    position: 'fixed',
    top: 0, left: '50%',
    transform: 'translateX(-50%)',
    width: '600px', height: '280px',
    background: 'radial-gradient(ellipse at top, rgba(116,200,210,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
    zIndex: 0,
  };

  const contentStyle = {
    position: 'relative',
    zIndex: 1,
    maxWidth: '400px',
    margin: '0 auto',
  };

  if (loading) return (
    <div style={{ ...bgStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={glowStyle} />
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CykerHeader />
        <div style={{
          width: '36px', height: '36px',
          border: `3px solid ${SURFACE2}`,
          borderTop: `3px solid ${TEAL}`,
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <div style={{ color: TEXT2, marginTop: '16px', fontSize: '14px' }}>Cargando viaje...</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ ...bgStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={glowStyle} />
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CykerHeader />
        <div style={{
          ...CARD, padding: '24px 28px', marginTop: '8px',
          color: TEXT1, fontSize: '15px', textAlign: 'center', maxWidth: '320px',
        }}>
          <div style={{ fontSize: '36px', marginBottom: '12px' }}>🔍</div>
          <div style={{ fontWeight: '600', marginBottom: '8px' }}>Viaje no encontrado</div>
          <div style={{ color: TEXT2, fontSize: '13px' }}>{error}</div>
        </div>
      </div>
    </div>
  );

  const finalizado = !!(trip.fin || trip.estado === 'finalizado');
  const importe    = parseFloat(trip.importe) || 0;
  const distKm     = numKm(trip.distancia_metros);
  const fichasDist = trip.fichas_distancia || 0;
  const fichasEsp  = trip.fichas_espera || 0;
  const chofer     = nombrePropio((trip.chofer_nombre || '') + ' ' + (trip.chofer_apellido || '')) || '-';
  const patente    = trip.patente ? String(trip.patente).toUpperCase() : '-';
  const aditamento = trip.aditamento || null;

  const tiempoLabel = finalizado
    ? fmtElapsed(trip.inicio, new Date(trip.fin).getTime())
    : fmtElapsed(trip.inicio, now);
  const tiempoEspera = fmtDuration(trip.tiempo_espera_segundos || 0);
  const labelMap = { 1: 'Muy malo', 2: 'Malo', 3: 'Regular', 4: 'Bueno', 5: 'Excelente ✨' };

  const verdict = osrmData ? (
    osrmData.pct <= 0   ? { text: '✓ Ruta eficiente — recorrido dentro de lo óptimo', bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.3)', color: GREEN }
    : osrmData.pct <= 25 ? { text: `✓ Recorrido normal (+${osrmData.pct}% vs ruta óptima)`, bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.3)', color: GREEN }
    : osrmData.pct <= 60 ? { text: `⚠ Recorrido más largo de lo esperado (+${osrmData.pct}%)`, bg: 'rgba(245,166,35,0.1)', border: 'rgba(245,166,35,0.3)', color: YELLOW }
    : { text: `🚨 Recorrido muy superior a la ruta óptima (+${osrmData.pct}%)`, bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)', color: RED }
  ) : null;

  return (
    <div style={bgStyle}>
      <div style={glowStyle} />
      <div style={contentStyle}>

        <CykerHeader aditamento={aditamento} />

        {/* ── TARIFA ── */}
        <div style={CARD}>
          <div style={{ height: '3px', background: TEAL }} />
          <div style={{ padding: '20px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: TEAL, letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '6px' }}>
              {finalizado ? 'Total a pagar' : 'Importe hasta ahora'}
            </div>
            <div style={{ fontSize: '58px', fontWeight: '800', color: TEXT1, lineHeight: 1.05, fontVariantNumeric: 'tabular-nums', letterSpacing: '-1px' }}>
              {pesos(importe)}
            </div>
            <StatusBadge finalizado={finalizado} />
          </div>
        </div>

        {/* ── VER TICKET (al finalizar, lo primero que busca el pasajero) ── */}
        {finalizado && (
          <a
            href={`${API_BASE}/viajes/qr/${qrToken}/ticket`}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: TEAL, color: '#06181A', borderRadius: '14px', padding: '15px 20px',
              marginBottom: '10px', fontWeight: '800', fontSize: '15px', textDecoration: 'none',
            }}
          >
            <span style={{ fontSize: '18px', marginRight: '10px' }}>🧾</span>
            Ver ticket
          </a>
        )}

        {/* ── COMPARTIR (siempre visible) ── */}
        <button
          onClick={() => {
            const url = window.location.href;
            if (navigator.share) {
              navigator.share({
                title: finalizado ? 'Mi viaje en Cyker' : '🚕 Seguí mi viaje en tiempo real',
                text: finalizado
                  ? `Comprobante de mi viaje en taxi — Cyker`
                  : `Estoy en un viaje. Podés seguir mi recorrido en tiempo real:`,
                url,
              });
            } else {
              navigator.clipboard?.writeText(url).then(() => alert('¡Enlace copiado!'));
            }
          }}
          style={{
            width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            background: finalizado ? SURFACE : 'rgba(116,200,210,0.12)',
            border: finalizado ? `1px solid ${BORDER}` : '1px solid rgba(116,200,210,0.35)',
            borderRadius: '14px',
            padding: '14px 20px',
            marginBottom: '12px',
            cursor: 'pointer',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
            <polyline points="16 6 12 2 8 6"/>
            <line x1="12" y1="2" x2="12" y2="15"/>
          </svg>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: finalizado ? TEAL : TEXT1 }}>
              {finalizado ? 'Compartir comprobante' : 'Compartir recorrido en vivo'}
            </div>
            {!finalizado && (
              <div style={{ fontSize: '11px', color: TEXT2, marginTop: '2px' }}>
                Un familiar puede seguirte en tiempo real
              </div>
            )}
          </div>
        </button>

        {/* ── MÉTRICAS ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
          <MetricCard label="Tiempo"  value={tiempoLabel} />
          <MetricCard label="Espera"  value={tiempoEspera} />
          <MetricCard label="Km"      value={distKm} />
        </div>

        {/* ── FICHAS ── */}
        <div style={{ ...CARD, padding: '16px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {[
            { label: 'Fichas por distancia', value: fichasDist },
            { label: 'Fichas por espera', value: fichasEsp },
          ].map(({ label, value }) => (
            <div key={label} style={{
              background: SURFACE2, borderRadius: '10px', padding: '12px',
              display: 'flex', flexDirection: 'column', gap: '4px',
              border: `1px solid ${BORDER}`,
            }}>
              <span style={{ fontSize: '10px', color: TEAL, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
              <span style={{ fontSize: '22px', fontWeight: '800', color: TEXT1, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
            </div>
          ))}
        </div>

        {/* ── INFO CHOFER ── */}
        <div style={{ ...CARD, padding: '16px 20px' }}>
          <div style={{ fontSize: '10px', color: TEAL, fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' }}>
            Información del viaje
          </div>
          {[{ label: 'Chofer', value: chofer }, { label: 'Patente', value: patente }].map(({ label, value }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: `1px solid ${BORDER}`, fontSize: '14px' }}>
              <span style={{ color: TEXT2 }}>{label}</span>
              <span style={{ fontWeight: '600', color: TEXT1 }}>{value}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', fontSize: '14px' }}>
            <span style={{ color: TEXT2 }}>Estado</span>
            <span style={{ fontWeight: '700', color: finalizado ? GREEN : TEAL }}>
              {finalizado ? '✓ Finalizado' : '● En curso'}
            </span>
          </div>
        </div>

        {/* ── MAPA INTERACTIVO (solo al finalizar) ── */}
        {finalizado && (
          <div style={{ ...CARD, padding: 0 }}>
            <div style={{ padding: '14px 16px 0', fontSize: '10px', color: TEAL, fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase' }}>
              Recorrido del viaje
            </div>

            <div style={{ position: 'relative', height: '280px', marginTop: '10px' }}>
              <div ref={mapContainerRef} style={{ height: '280px', width: '100%' }} />
              {mapInitialized && (
                <a href={`/recorrido/${encodeURIComponent(qrToken)}`} aria-label="Ver el mapa en pantalla completa"
                   style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 500 }} />
              )}
              {!mapInitialized && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  background: SURFACE2, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999,
                }}>
                  <span style={{ color: TEXT2, fontSize: '14px' }}>
                    {positions === null ? '🗺️ Cargando mapa...' : positions.length < 2 ? '📍 Sin datos de ruta' : '🗺️ Iniciando mapa...'}
                  </span>
                </div>
              )}
            </div>

            {mapInitialized && (
              <div style={{ padding: '8px 16px', display: 'flex', gap: '14px', flexWrap: 'wrap', borderTop: `1px solid ${BORDER}` }}>
                <LegendItem color={ROUTE_BLUE} label="Ruta real" type="line" />
                {osrmData && <LegendItem color={OPT_ORANGE} label="Ruta óptima" type="line" />}
                <LegendItem color={GREEN}  label="Inicio" type="dot" />
                <LegendItem color={RED}    label="Fin"    type="dot" />
                <a href={`/recorrido/${encodeURIComponent(qrToken)}`} style={{ marginLeft: 'auto', color: TEAL, fontSize: '12px', fontWeight: '700' }}>
                  Tocá el mapa para verlo en pantalla completa
                </a>
              </div>
            )}

            {(startAddress || endAddress) && (
              <div style={{ padding: '0 16px', marginBottom: '10px' }}>
                {startAddress && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                    <div style={{ width: '10px', height: '10px', background: GREEN, borderRadius: '50%', flexShrink: 0, marginTop: '3px' }} />
                    <div style={{ fontSize: '12px', color: TEXT2, lineHeight: 1.4 }}>
                      <span style={{ fontWeight: '700', color: GREEN, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>Inicio</span>
                      {startAddress}
                    </div>
                  </div>
                )}
                {endAddress && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ width: '10px', height: '10px', background: RED, borderRadius: '50%', flexShrink: 0, marginTop: '3px' }} />
                    <div style={{ fontSize: '12px', color: TEXT2, lineHeight: 1.4 }}>
                      <span style={{ fontWeight: '700', color: RED, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>Fin</span>
                      {endAddress}
                    </div>
                  </div>
                )}
              </div>
            )}

            {osrmData && verdict && (
              <div style={{ padding: '4px 16px 14px' }}>
                <div style={{ fontSize: '10px', color: TEAL, fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '8px' }}>
                  Comparación de ruta
                </div>
                {[
                  { label: 'Real',   km: osrmData.realKm, barPct: osrmData.realBarPct, color: ROUTE_BLUE },
                  { label: 'Óptima', km: osrmData.optKm,  barPct: osrmData.optBarPct,  color: OPT_ORANGE },
                ].map(({ label, km, barPct, color }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                    <div style={{ width: '40px', fontSize: '11px', color: TEXT2 }}>{label}</div>
                    <div style={{ flex: 1, background: SURFACE2, borderRadius: '4px', height: '7px' }}>
                      <div style={{ width: barPct + '%', background: color, borderRadius: '4px', height: '7px', transition: 'width 0.8s ease' }} />
                    </div>
                    <div style={{ width: '54px', fontSize: '11px', fontWeight: '700', color: TEXT1, textAlign: 'right' }}>{km} km</div>
                  </div>
                ))}
                <div style={{
                  marginTop: '8px', fontSize: '12px', fontWeight: '600',
                  padding: '7px 10px', borderRadius: '8px',
                  background: verdict.bg, border: `1px solid ${verdict.border}`, color: verdict.color,
                }}>
                  {verdict.text}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── PRECISIÓN GPS ── */}
        {finalizado && trip.distancia_mapa && trip.precision_pct != null && parseFloat(trip.mapbox_confidence) >= 0.75 && (() => {
          const distCyker = parseFloat(trip.distancia_metros) || 0;
          const distMapa = parseFloat(trip.distancia_mapa) || 0;
          const precPct = parseFloat(trip.precision_pct);
          const absPct = Math.abs(precPct);
          const maxD = Math.max(distCyker, distMapa);
          const conf = parseFloat(trip.mapbox_confidence);
          const vrd = absPct <= 1
            ? { text: `Precisión INTI (${pctTxt(precPct)})`, bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.3)', color: GREEN }
            : absPct <= 2
            ? { text: `Precisión en servicio (${pctTxt(precPct)})`, bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.3)', color: GREEN }
            : absPct <= 5
            ? { text: `Desvío moderado (${pctTxt(precPct)})`, bg: 'rgba(245,166,35,0.1)', border: 'rgba(245,166,35,0.3)', color: YELLOW }
            : { text: `Desvío alto (${pctTxt(precPct)})`, bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)', color: RED };
          return (
            <div style={{ ...CARD, padding: '16px 20px' }}>
              <div style={{ fontSize: '10px', color: TEAL, fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '10px' }}>
                Precisión GPS
              </div>
              {[
                { label: 'Cyker', km: numKm(distCyker), barPct: Math.round(distCyker / maxD * 100), color: TEAL },
                { label: 'Mapa', km: numKm(distMapa), barPct: Math.round(distMapa / maxD * 100), color: '#7C4DFF' },
              ].map(({ label, km, barPct, color }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                  <div style={{ width: '40px', fontSize: '11px', color: TEXT2 }}>{label}</div>
                  <div style={{ flex: 1, background: SURFACE2, borderRadius: '4px', height: '7px' }}>
                    <div style={{ width: barPct + '%', background: color, borderRadius: '4px', height: '7px', transition: 'width 0.8s ease' }} />
                  </div>
                  <div style={{ width: '54px', fontSize: '11px', fontWeight: '700', color: TEXT1, textAlign: 'right' }}>{km} km</div>
                </div>
              ))}
              <div style={{
                marginTop: '8px', fontSize: '12px', fontWeight: '600',
                padding: '7px 10px', borderRadius: '8px',
                background: vrd.bg, border: `1px solid ${vrd.border}`, color: vrd.color,
              }}>
                {vrd.text}
              </div>
              <div style={{ marginTop: '6px', fontSize: '10px', color: TEXT2, textAlign: 'right' }}>
                Confianza: {(conf * 100).toFixed(0)}% · Ref: Mapbox Map Matching
              </div>
            </div>
          );
        })()}

        {/* ── CALIFICACIÓN ── */}
        <div style={{ ...CARD, padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: TEXT1, marginBottom: '14px' }}>
            ¿Cómo fue el servicio?
          </div>
          {ratingDone ? (
            <div style={{ color: TEAL, fontWeight: '600', fontSize: '15px', padding: '6px' }}>
              ⭐ ¡Gracias por calificar con {rating} estrella{rating !== 1 ? 's' : ''}!
            </div>
          ) : (
            <>
              <Stars value={rating} onChange={submitRating} disabled={ratingLoading} />
              <div style={{ fontSize: '13px', color: TEXT2, marginTop: '8px', minHeight: '18px' }}>
                {ratingLoading ? 'Enviando...' : (rating > 0 ? labelMap[rating] : 'Tocá una estrella para calificar')}
              </div>
            </>
          )}
        </div>

        {/* ── DENUNCIA ── */}
        {denunciaDone ? (
          <div style={{ ...CARD, padding: '16px', textAlign: 'center', color: GREEN, fontSize: '14px' }}>
            ✓ Tu denuncia fue recibida. Gracias.
          </div>
        ) : !denunciaOpen ? (
          <button
            onClick={() => setDenunciaOpen(true)}
            style={{
              width: '100%', padding: '13px',
              background: 'transparent',
              border: `1px solid ${BORDER}`,
              borderRadius: '12px', color: TEXT2, fontSize: '13px', fontWeight: '500',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px',
            }}
          >
            🚨 Denunciar al chofer
          </button>
        ) : (
          <div style={{ ...CARD, padding: '20px' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: RED, marginBottom: '12px' }}>
              🚨 Denunciar al chofer
            </div>
            <textarea
              value={denunciaText}
              onChange={e => setDenunciaText(e.target.value)}
              placeholder="Describí qué ocurrió..."
              rows={4}
              style={{
                width: '100%', padding: '12px',
                border: `1px solid ${BORDER}`, borderRadius: '10px',
                fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', outline: 'none',
                boxSizing: 'border-box',
                background: SURFACE2, color: TEXT1,
              }}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
              <button
                onClick={() => { setDenunciaOpen(false); setDenunciaText(''); }}
                style={{
                  flex: 1, padding: '12px',
                  background: SURFACE2, border: `1px solid ${BORDER}`,
                  borderRadius: '10px', fontSize: '14px', color: TEXT2, cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={submitDenuncia}
                disabled={!denunciaText.trim() || denunciaLoading}
                style={{
                  flex: 2, padding: '12px',
                  background: denunciaText.trim() ? 'rgba(239,68,68,0.15)' : SURFACE2,
                  border: denunciaText.trim() ? '1px solid rgba(239,68,68,0.4)' : `1px solid ${BORDER}`,
                  borderRadius: '10px', fontSize: '14px', fontWeight: '700',
                  color: denunciaText.trim() ? RED : TEXT2,
                  cursor: denunciaText.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {denunciaLoading ? 'Enviando...' : 'Enviar denuncia'}
              </button>
            </div>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '8px', color: '#3D444D', fontSize: '11px' }}>
          Viaje N.º {trip.id} - Cyker, taxímetro digital
        </div>

      </div>

      <style>{`
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.25} }
        @keyframes spin   { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

function LegendItem({ color, label, type }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginRight: '12px', fontSize: '11px', color: TEXT2 }}>
      {type === 'dot'  && <div style={{ width: '10px', height: '10px', background: color, borderRadius: '50%', flexShrink: 0, marginRight: '5px' }} />}
      {type === 'line' && <div style={{ width: '22px', height: '3px', background: color, borderRadius: '2px', flexShrink: 0, marginRight: '5px' }} />}
      {label}
    </div>
  );
}

export default App;
